import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import styles from './ProductForm.module.css';
import ProductAttributesModal from './ProductAttributesModal';

const defaultForm = {
  name: '',
  price: '',
  original_price: '',
  cost_price: '',
  description: '',
  short_description: '',
  stock_quantity: '',
  category_id: '',
  brand: '',
  weight: '',
  dimensions: '',
  is_active: true,
  is_featured: false,
  is_prime: false,
  is_primary: true,
};

const ProductForm = ({ categories, onProductCreated, product }) => {
  const [form, setForm] = useState(defaultForm);
  const [existingImages, setExistingImages] = useState([]);
  // Actualiza el formulario si se recibe un producto (edición)
  useEffect(() => {
    if (product) {
      setForm({
        ...defaultForm,
        ...product,
        price: product.price ?? '',
        original_price: product.original_price ?? '',
        cost_price: product.cost_price ?? '',
        stock_quantity: product.stock_quantity ?? '',
        weight: product.weight ?? '',
        dimensions: product.dimensions ? JSON.stringify(product.dimensions) : '',
        category_id: product.category_id ?? '',
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        is_prime: product.is_prime ?? false,
        is_primary: true,
      });
      // Cargar imágenes existentes desde product_images (GET)
      apiService.getProductImages(product.id).then(data => {
        setExistingImages(data.images || []);
      });
    } else {
      setForm(defaultForm);
      setExistingImages([]);
    }
  }, [product]);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAttrModal, setShowAttrModal] = useState(false);
  const [attributes, setAttributes] = useState([]);
  // productId is intentionally not stored separately - use product?.id when needed

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    setImageFiles(Array.from(e.target.files));
  };

  const handleDeleteExistingImage = async (imageId) => {
    const image = existingImages.find(img => img.id === imageId);
    if (!image) return;
    try {
      await apiService.request(`/products/${product.id}/images`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: apiService.token ? `Bearer ${apiService.token}` : undefined
        },
        body: JSON.stringify({ imageId: image.id, cloudflareUrl: image.cloudflare_url })
      });
      setExistingImages(existingImages.filter(img => img.id !== imageId));
    } catch (err) {
      setError('No se pudo borrar la imagen: ' + (err.message || 'Error desconocido'));
    }
  };

  // Si es edición, carga los atributos del producto
  useEffect(() => {
    if (product?.id) {
      apiService.getProductAttributes(product.id)
        .then(data => setAttributes(data.attributes || []))
        .catch(() => setAttributes([]));
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1. Crear o actualizar producto (sin campos de imágenes)
      const productData = {
        ...(() => {
          const { is_primary, ...rest } = form;
          return rest;
        })(),
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        stock_quantity: parseInt(form.stock_quantity),
        weight: form.weight ? parseFloat(form.weight) : null,
        dimensions: form.dimensions ? JSON.parse(form.dimensions) : null
      };
      let prodData;
      if (product?.id) {
        prodData = await apiService.updateProduct(product.id, productData);
      } else {
        prodData = await apiService.createProduct(productData);
      }
      if (!prodData.id) throw new Error(prodData.error || 'Error al guardar producto');

      // 2. Subir imágenes solo si hay cambios y ya existe el id
      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          const formData = new FormData();
          formData.append('image', file);
          formData.append('isPrimary', 'false');
          await fetch(`${apiService.baseURL}/products/${prodData.id}/images`, {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: apiService.token ? `Bearer ${apiService.token}` : undefined
            }
          });
        }
      }
      // Eliminar imágenes (puedes implementar DELETE en el backend si lo necesitas)
      // Aquí solo se marcan para eliminar, pero no se hace la petición DELETE

      // 3. Guardar atributos personalizados
      if (attributes.length > 0) {
        await apiService.createProductAttribute(product?.id || prodData.id, attributes);
      }

      setSuccess(product?.id ? 'Producto actualizado correctamente' : 'Producto creado correctamente');
      setForm(defaultForm);
      setImageFiles([]);
  setExistingImages([]);
      setAttributes([]);
      if (onProductCreated) onProductCreated(prodData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
        <div className={styles.row}>
          <label>
            Nombre:
            <input name="name" value={form.name} onChange={handleChange} required />
          </label>
          <label>
            Precio:
            <input name="price" type="number" step="0.01" value={form.price} onChange={handleChange} required />
          </label>
          <label>
            Precio original:
            <input name="original_price" type="number" step="0.01" value={form.original_price} onChange={handleChange} />
          </label>
          <label>
            Precio de costo:
            <input name="cost_price" type="number" step="0.01" value={form.cost_price} onChange={handleChange} />
          </label>
        </div>
        <div className={styles.row}>
          <label>
            Descripción:
            <textarea name="description" value={form.description} onChange={handleChange} required />
          </label>
          <label>
            Descripción corta:
            <input name="short_description" value={form.short_description} onChange={handleChange} />
          </label>
        </div>
        <div className={styles.row}>
          <label>
            Stock:
            <input name="stock_quantity" type="number" value={form.stock_quantity} onChange={handleChange} required />
          </label>
          <label>
            Categoría:
            <select
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
              required
              className={styles.comboBox}
            >
              <option value="">Selecciona una categoría</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </label>
          <label>
            Marca:
            <input name="brand" value={form.brand} onChange={handleChange} />
          </label>
        </div>
        <div className={styles.row}>
          <label>
            Peso:
            <input name="weight" type="number" step="0.01" value={form.weight} onChange={handleChange} />
          </label>
          <label>
            Dimensiones (JSON):
            <input name="dimensions" value={form.dimensions} onChange={handleChange} placeholder='{"width":10,"height":5,"depth":2}' />
          </label>
        </div>
        <div className={styles.row}>
          <label>
            Activo:
            <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} />
          </label>
          <label className={styles.checkboxLabel}>
            Prime:
            <input name="is_prime" type="checkbox" checked={form.is_prime} onChange={handleChange} />
          </label>
          <label className={styles.checkboxLabel}>
            Destacado:
            <input name="is_featured" type="checkbox" checked={form.is_featured} onChange={handleChange} />
          </label>
          <label>
            Imagen principal:
            <input name="is_primary" type="checkbox" checked={form.is_primary} onChange={handleChange} />
          </label>
        </div>
        <div className={styles.row}>
          <label>
            Imágenes:
            <input type="file" accept="image/*" multiple onChange={handleImageChange} />
          </label>
          {existingImages.length > 0 && (
            <div className={styles.imagePreviewList}>
              <span>Imágenes actuales:</span>
              <div className={styles.imagePreviewGrid}>
                {existingImages.map(img => (
                  <div key={img.id} className={styles.imagePreviewItem}>
                    <img src={img.cloudflare_url} alt="Producto" className={styles.imagePreview} />
                    <button type="button" className={styles.deleteImageBtn} onClick={() => handleDeleteExistingImage(img.id)}>
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.attrButton}
            onClick={() => setShowAttrModal(true)}
          >
            Atributos personalizados
          </button>
        </div>
        <button type="submit" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</button>
      </form>
      {showAttrModal && (
        <ProductAttributesModal
          attributes={attributes}
          setAttributes={setAttributes}
          onClose={() => setShowAttrModal(false)}
        />
      )}
    </>
  );
};

export default ProductForm;